/**
 * MCP tools: the structural scene surface — inspect, create, modify,
 * delete, select, view-control, layers, document actions and viewport
 * screenshots. These cover what a typed command line cannot express
 * programmatically (state inspection, batch data edits, pixel capture).
 */

import { v4 as uuidv4 } from 'uuid';
import { useAppStore } from '../../state/store';
import { useBIMStore } from '../../state/useBIMStore';
import { useDocumentStore } from '../../state/useDocumentStore';
import type { SceneObject } from '../../core/scene';
import { bimKernel } from '../../core/BIMKernel';
import { geometryExporter } from '../../core/services';
import { exportToIFC } from '../../tools/IFCTools';
import { saveCurrentDocument, loadDocumentFromData } from '../../utils/ProjectFileIO';
import { refreshKernelTessellation, entityToPolyline } from '../../core/commands/runners/lib';
import { writeDxfNative, DEFAULT_DXF_VERSION } from '../cad/dxf/writeDxfNative';
import { resolveDxfVersion } from '../cad/dxf/types';
import { listChildren, ROOT_FOLDER_ID, type VfsEntry } from '../fileSystem/virtualFs';
import { captureViewportPng } from './viewportCapture';
import { requestCameraFraming } from '../../rendering/cameraTick';
import { useBlockStore } from '../../state/commandAuxStores';
import { newProject as resetCadSession } from '../../utils/FileOperations';
import type { McpToolHandler } from './mcpResultUtils';
import {
    asBoolean, asNumber, asString, asStringArray, asVec3, fail, okImage, okJson, okText, safeJsonStringify,
} from './mcpResultUtils';
import type { BIMElement, DoorElement, FloorElement, RoofElement, WallElement, WindowElement } from '../../core/types';
import {
    buildDimensionElement,
    buildHostedOpeningElement,
    buildLevelElement,
    buildRoofElement,
    buildSlabElement,
    buildWallElement,
} from './mcpBimBuilders';

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

/** Compact ledger row for query results. */
function sceneRow(o: SceneObject) {
    return {
        id: o.id,
        source: o.source,
        kind: o.kind,
        name: o.displayName,
        layerId: o.layerId,
        visible: o.visible,
        locked: o.locked,
        position: o.placement.position,
        bounds: o.bounds,
    };
}

async function findVfsFile(name: string): Promise<VfsEntry | null> {
    const target = name.toLowerCase().replace(/\.(yap|yate)$/i, '');
    const queue: string[] = [ROOT_FOLDER_ID];
    while (queue.length) {
        const folderId = queue.shift() as string;
        const children = await listChildren(folderId);
        for (const child of children) {
            if (child.type === 'folder') {
                queue.push(child.id);
            } else if (child.name.toLowerCase().replace(/\.(yap|yate)$/i, '') === target) {
                return child;
            }
        }
    }
    return null;
}

// ---------------------------------------------------------------------------
// scene query / inspection
// ---------------------------------------------------------------------------

export const handleQueryScene: McpToolHandler = (args) => {
    const source = asString(args.source) as 'bim' | 'cad' | 'nurbs' | undefined;
    const kind = asString(args.kind)?.toLowerCase();
    const nameContains = asString(args.nameContains)?.toLowerCase();
    const includeHidden = asBoolean(args.includeHidden) ?? true;
    const limit = Math.min(Math.max(asNumber(args.limit) ?? 200, 1), 5000);

    const ledger = Object.values(useDocumentStore.getState().sceneObjects);
    let rows = ledger;
    if (source) rows = rows.filter((o) => o.source === source);
    if (kind) rows = rows.filter((o) => o.kind.toLowerCase() === kind);
    if (nameContains) rows = rows.filter((o) => o.displayName.toLowerCase().includes(nameContains));
    if (!includeHidden) rows = rows.filter((o) => o.visible && !o.locked);

    const truncated = rows.length > limit;
    return okJson(
        {
            total: rows.length,
            returned: Math.min(rows.length, limit),
            truncated,
            objects: rows.slice(0, limit).map(sceneRow),
        },
        `${rows.length} object(s) matched the filter.`,
    );
};

export const handleGetElement: McpToolHandler = (args) => {
    const id = asString(args.id);
    if (!id) return fail("Missing required argument 'id'.");

    const bim = useBIMStore.getState();
    const element = bim.project.elements.get(id);
    if (element) {
        return okJson(
            JSON.parse(safeJsonStringify({ kind: 'bim', element })),
            `BIM element ${element.name || element.type} (${element.category}).`,
        );
    }

    const entity = useAppStore.getState().entities[id];
    if (entity) {
        return okJson(
            JSON.parse(safeJsonStringify({ kind: 'cad', entity })),
            `CAD entity ${entity.name || entity.type} (type: ${entity.type}).`,
        );
    }

    const ledgerRecord = useDocumentStore.getState().sceneObjects[id];
    if (ledgerRecord) {
        return okJson(
            JSON.parse(safeJsonStringify({ kind: 'ledger', object: ledgerRecord })),
            `Scene ledger record (${ledgerRecord.source}/${ledgerRecord.kind}).`,
        );
    }
    return fail(`No object found with id '${id}'. Use yap_query_scene to list ids.`);
};

// ---------------------------------------------------------------------------
// structural mutation
// ---------------------------------------------------------------------------

export const handleCreateElement: McpToolHandler = (args) => {
    const category = asString(args.category);
    if (!category) return fail("Missing required argument 'category' (e.g. 'Walls', 'Doors').");
    const bim = useBIMStore.getState();

    // Architectural categories get a complete renderable geometryData built
    // from the explicit args (walls) or the host wall (doors/windows). A bare
    // parameter-bag wall used to be committed without geometryData, which then
    // crashed the wall-join solver on every later wall add.
    if (category === 'Walls') {
        const built = buildWallElement(args, bim.project.levels as unknown as Map<string, BIMElement>);
        if (!built.ok) return fail(built.error);
        const id = uuidv4();
        const element: WallElement = {
            id,
            name: asString(args.name) ?? `Walls ${bim.project.elements.size + 1}`,
            category: 'Walls',
            type: built.value.type,
            parameters: built.value.parameters,
            constraints: [],
            geometryData: built.value.geometryData,
        };
        bim.addElement(element);
        return okJson({ id }, `Created wall '${element.name}' (type: ${built.value.type}). Element id: ${id}`);
    }
    if (category === 'Doors' || category === 'Windows') {
        const built = buildHostedOpeningElement(args, category, bim.project.elements);
        if (!built.ok) return fail(built.error);
        const id = uuidv4();
        const base = {
            id,
            name: asString(args.name) ?? `${category} ${bim.project.elements.size + 1}`,
            category,
            type: built.value.type,
            parameters: built.value.parameters,
            constraints: built.value.constraints,
            geometryData: built.value.geometryData,
        };
        const element = category === 'Doors' ? (base as DoorElement) : (base as WindowElement);
        bim.addElement(element);
        return okJson(
            { id },
            `Created ${category.slice(0, -1).toLowerCase()} '${element.name}' ` +
                `(host wall ${built.value.geometryData.hostWallId}, position ${built.value.geometryData.position}). Element id: ${id}`,
        );
    }

    // Levels are not scene elements — they commit through the kernel, which
    // also auto-creates the matching floor-plan view.
    if (category === 'Levels') {
        const built = buildLevelElement(args, bim.project.levels);
        if (!built.ok) return fail(built.error);
        const level = bimKernel.addLevel(built.value.name, built.value.elevation);
        useBIMStore.getState()._refresh();
        return okJson({ id: level.id, name: level.name, elevation: level.elevation },
            `Created level '${level.name}' at ${level.elevation} mm (id: ${level.id}).`);
    }
    if (category === 'Floors' || category === 'Ceilings') {
        const built = buildSlabElement(args, category, bim.project.levels);
        if (!built.ok) return fail(built.error);
        const id = uuidv4();
        const element: FloorElement = {
            id,
            name: asString(args.name) ?? `${category} ${bim.project.elements.size + 1}`,
            category: category as 'Floors',
            type: built.value.type,
            parameters: built.value.parameters,
            constraints: [],
            geometryData: built.value.geometryData,
        };
        bim.addElement(element);
        return okJson({ id }, `Created ${category.slice(0, -1).toLowerCase()} '${element.name}' ` +
            `(${built.value.geometryData.boundary.length} boundary points, offset ${(built.value.geometryData.heightOffsetFromLevel * 1000).toFixed(0)} mm). Element id: ${id}`);
    }
    if (category === 'Roofs') {
        const built = buildRoofElement(args, bim.project.levels);
        if (!built.ok) return fail(built.error);
        const id = uuidv4();
        const element: RoofElement = {
            id,
            name: asString(args.name) ?? `Roofs ${bim.project.elements.size + 1}`,
            category: 'Roofs',
            type: built.value.type,
            parameters: built.value.parameters,
            constraints: [],
            geometryData: built.value.geometryData,
        };
        bim.addElement(element);
        return okJson({ id }, `Created roof '${element.name}' (type: ${built.value.geometryData.roofType}, slope: ${built.value.geometryData.slope}°). Element id: ${id}`);
    }
    if (category === 'Dimensions') {
        const built = buildDimensionElement(args);
        if (!built.ok) return fail(built.error);
        const id = uuidv4();
        const element = {
            id,
            name: asString(args.name) ?? `Dimensions ${bim.project.elements.size + 1}`,
            category,
            type: built.value.type,
            parameters: built.value.parameters,
            constraints: [],
            geometryData: built.value.geometryData,
        };
        bim.addElement(element);
        return okJson({ id }, `Created dimension '${element.name}'. Element id: ${id}`);
    }

    const type = asString(args.type) ?? category;
    const position = asVec3(args.position) ?? [0, 0, 0];
    const parameters = (args.parameters && typeof args.parameters === 'object')
        ? { ...(args.parameters as Record<string, unknown>), location: position }
        : { location: position };

    const id = uuidv4();
    const element = {
        id,
        name: asString(args.name) ?? `${category} ${bim.project.elements.size + 1}`,
        category,
        type,
        parameters,
        constraints: [],
        // Annotation categories (Dimensions, Tags, Text Notes) and most
        // structural kinds render from geometryData; without a payload the
        // element is invisible in the scene, so expose it here.
        ...(args.geometryData && typeof args.geometryData === 'object'
            ? { geometryData: args.geometryData }
            : {}),
    } as never;
    bim.addElement(element);
    return okJson({ id }, `Created ${category} element '${(element as { name: string }).name}' (id: ${id}).`);
};

export const handleUpdateElement: McpToolHandler = (args) => {
    const id = asString(args.id);
    if (!id) return fail("Missing required argument 'id'.");
    const changes: string[] = [];

    const bim = useBIMStore.getState();
    const app = useAppStore.getState();
    const doc = useDocumentStore.getState();

    const element = bim.project.elements.get(id);
    if (element) {
        const updates: Record<string, unknown> = {};
        const name = asString(args.name);
        if (name) updates.name = name;
        const parameters = args.parameters;
        if (parameters && typeof parameters === 'object') {
            updates.parameters = { ...(element as { parameters: Record<string, unknown> }).parameters, ...(parameters as Record<string, unknown>) };
        }
        const visible = asBoolean(args.visible);
        if (visible !== undefined) {
            // Visibility is protocol-level: mirror it onto the ledger record.
            doc.patchSceneObject(id, { visible });
        }
        const locked = asBoolean(args.locked);
        if (locked !== undefined) {
            doc.patchSceneObject(id, { locked });
        }
        if (Object.keys(updates).length > 0) {
            bim.updateElement(id, updates as never);
            changes.push(`bim: ${Object.keys(updates).join(', ')}`);
        }
        if (visible !== undefined || locked !== undefined) {
            changes.push('visibility/lock via scene ledger');
        }
    } else if (app.entities[id]) {
        const updates: Record<string, unknown> = {};
        const name = asString(args.name);
        if (name) updates.name = name;
        const visible = asBoolean(args.visible);
        if (visible !== undefined) updates.visible = visible;
        const locked = asBoolean(args.locked);
        if (locked !== undefined) updates.locked = locked;
        const data = args.data;
        if (data && typeof data === 'object') {
            const merged = { ...app.entities[id].data, ...(data as Record<string, unknown>) };
            // Kernel citizens: patching curve/surface data must regenerate
            // the tessellation cache or the viewport would keep showing the
            // stale display mesh (refreshKernelTessellation is the contract).
            if (merged.curve || merged.surface) {
                updates.data = refreshKernelTessellation(merged);
            } else {
                updates.data = merged;
            }
        }
        if (Object.keys(updates).length > 0) {
            app.updateEntity(id, updates as never);
            changes.push(`cad: ${Object.keys(updates).join(', ')}`);
        }
    } else if (doc.sceneObjects[id]) {
        const patch: Record<string, unknown> = {};
        const name = asString(args.name);
        if (name) patch.displayName = name;
        const visible = asBoolean(args.visible);
        if (visible !== undefined) patch.visible = visible;
        const locked = asBoolean(args.locked);
        if (locked !== undefined) patch.locked = locked;
        if (Object.keys(patch).length > 0) {
            doc.patchSceneObject(id, patch as never);
            changes.push(`ledger: ${Object.keys(patch).join(', ')}`);
        }
    } else {
        return fail(`No object found with id '${id}'.`);
    }

    if (changes.length === 0) return fail('No updatable fields provided (name/visible/locked/parameters/data).');
    return okText(`Updated ${id} — ${changes.join('; ')}.`);
};

export const handleDeleteElements: McpToolHandler = (args) => {
    const ids = asStringArray(args.ids);
    if (!ids || ids.length === 0) return fail("Missing required argument 'ids'.");
    const bim = useBIMStore.getState();
    const app = useAppStore.getState();
    let deleted = 0;
    const missed: string[] = [];
    for (const id of ids) {
        if (bim.project.elements.has(id)) {
            bim.removeElement(id);
            deleted += 1;
        } else if (app.entities[id]) {
            app.deleteEntity(id);
            deleted += 1;
        } else {
            missed.push(id);
        }
    }
    const text = `Deleted ${deleted}/${ids.length} object(s).` + (missed.length ? ` Not found: ${missed.slice(0, 10).join(', ')}.` : '');
    return deleted > 0 ? okJson({ deleted, missed }, text) : fail(text);
};

export const handleSelectElements: McpToolHandler = (args) => {
    const mode = asString(args.mode) ?? 'replace';
    const app = useAppStore.getState();
    const bim = useBIMStore.getState();

    const mirrorBim = (ids: string[]) => bim.setSelection(ids);

    if (mode === 'clear') {
        app.clearSelection();
        mirrorBim([]);
        return okText('Selection cleared.');
    }
    if (mode === 'all') {
        const ids = new Set<string>(app.selectedIds);
        Object.keys(app.entities).forEach((id) => ids.add(id));
        Object.keys(useDocumentStore.getState().sceneObjects).forEach((id) => ids.add(id));
        const list = Array.from(ids);
        app.select(list);
        mirrorBim(list);
        return okText(`Selected all: ${list.length} object(s).`);
    }
    const ids = asStringArray(args.ids);
    if (!ids || ids.length === 0) return fail("Missing required argument 'ids' (or use mode clear/all).");
    if (mode === 'add') app.addToSelection(ids);
    else app.select(ids);
    mirrorBim(app.selectedIds);
    return okText(`Selection: ${app.selectedIds.length} object(s) selected.`);
};

// ---------------------------------------------------------------------------
// viewport
// ---------------------------------------------------------------------------

export const handleControlView: McpToolHandler = (args) => {
    const action = asString(args.action);
    const app = useAppStore.getState();
    switch (action) {
        case 'setView': {
            const view = asString(args.view);
            if (!view || !['top', 'front', 'right', 'perspective'].includes(view)) {
                return fail("setView requires 'view' = top|front|right|perspective.");
            }
            window.dispatchEvent(new CustomEvent('yap:set-view', { detail: { view } }));
            return okText(`View orientation set to '${view}'.`);
        }
        case 'zoomFit':
            window.dispatchEvent(new CustomEvent('yap:zoom-to-fit'));
            return okText('Zoomed to fit all geometry.');
        case 'zoomSelection':
            window.dispatchEvent(new CustomEvent('yap:zoom-to-selection'));
            return okText('Zoomed to selection.');
        case 'zoomRegion': {
            const a = asVec3(args.corner1);
            const b = asVec3(args.corner2);
            if (!a || !b) return fail('zoomRegion requires corner1 and corner2 ([x, y, z] world ground points).');
            requestCameraFraming('region', {
                a: [a[0], a[1], a[2] ?? 0],
                b: [b[0], b[1], b[2] ?? 0],
            });
            return okText('Zoomed to the corner window (CAD ZOOM W).');
        }
        case 'zoomScale': {
            const factor = asNumber(args.factor);
            if (!factor || factor <= 0) return fail("zoomScale requires 'factor' (>0; 2 = twice as close).");
            requestCameraFraming('scale', { factor });
            return okText(`Zoom scale ${factor}x applied.`);
        }
        case 'pan': {
            const dx = asNumber(args.dx) ?? 0;
            const dy = asNumber(args.dy) ?? 0;
            requestCameraFraming('pan', { dx, dy });
            return okText(`Panned by ${dx}, ${dy}.`);
        }
        case 'displayMode': {
            const mode = asString(args.mode);
            const valid = ['wireframe', 'shaded', 'rendered', 'xray', 'ghosted', 'hiddenLine'];
            if (!mode || !valid.includes(mode)) {
                return fail(`displayMode requires 'mode' one of: ${valid.join(', ')}.`);
            }
            app.updateView({ viewMode: mode as never });
            return okText(`Display mode set to '${mode}'.`);
        }
        case 'toggleGrid':
            app.updateView({ gridVisible: !app.view.gridVisible });
            return okText(`Grid ${app.view.gridVisible ? 'on' : 'off'}.`);
        case 'toggleAxes':
            app.updateView({ axesVisible: !app.view.axesVisible });
            return okText(`Axes ${app.view.axesVisible ? 'on' : 'off'}.`);
        case 'setCamera': {
            const position = asVec3(args.position);
            const target = asVec3(args.target);
            if (!position && !target) return fail('setCamera requires position and/or target ([x,y,z]).');
            const camera = { ...app.view.camera };
            if (position) camera.position = position;
            if (target) camera.target = target;
            app.updateView({ camera });
            return okText('Camera pose updated from the bridge.');
        }
        default:
            return fail("Unknown action. Expected setView|zoomFit|zoomSelection|zoomRegion|zoomScale|pan|displayMode|toggleGrid|toggleAxes|setCamera.");
    }
};

export const handleScreenshot: McpToolHandler = async () => {
    try {
        const base64 = await captureViewportPng();
        return okImage(base64, 'Viewport captured as PNG.');
    } catch (error) {
        return fail(`Screenshot failed: ${error instanceof Error ? error.message : String(error)}`);
    }
};

// ---------------------------------------------------------------------------
// layers
// ---------------------------------------------------------------------------

export const handleListLayers: McpToolHandler = () => {
    const app = useAppStore.getState();
    const layers = Object.values(app.layers).map((l) => ({
        id: l.id, name: l.name, color: l.color, visible: l.visible, locked: l.locked, frozen: !!l.frozen,
    }));
    const categories = new Map<string, number>();
    for (const o of Object.values(useDocumentStore.getState().sceneObjects)) {
        if (o.source === 'bim') categories.set(o.layerId, (categories.get(o.layerId) ?? 0) + 1);
    }
    return okJson({
        activeLayerId: app.activeLayerId,
        layers,
        bimCategories: Array.from(categories.entries()).map(([name, count]) => ({ name, count })),
    }, `${layers.length} layer(s).`);
};

export const handleManageLayers: McpToolHandler = (args) => {
    const action = asString(args.action);
    const app = useAppStore.getState();
    const target = asString(args.layerId);
    const findLayer = () =>
        Object.values(app.layers).find((l) => l.id === target || l.name.toLowerCase() === target?.toLowerCase());

    switch (action) {
        case 'create': {
            const name = asString(args.name);
            if (!name) return fail('create requires a layer name.');
            if (Object.values(app.layers).some((l) => l.name.toLowerCase() === name.toLowerCase())) {
                return fail(`Layer '${name}' already exists.`);
            }
            const id = `layer-${uuidv4().slice(0, 8)}`;
            app.addLayer({
                id, name, color: asString(args.color) ?? '#cccccc',
                visible: true, locked: false, order: Object.keys(app.layers).length,
            });
            return okText(`Created layer '${name}' (${id}).`);
        }
        case 'update': {
            if (!target) return fail('update requires layerId (id or name).');
            const layer = findLayer();
            if (!layer) return fail(`No layer '${target}'.`);
            const patch: Record<string, unknown> = {};
            const name = asString(args.name); if (name) patch.name = name;
            const color = asString(args.color); if (color) patch.color = color;
            const visible = asBoolean(args.visible); if (visible !== undefined) patch.visible = visible;
            const locked = asBoolean(args.locked); if (locked !== undefined) patch.locked = locked;
            const frozen = asBoolean(args.frozen); if (frozen !== undefined) patch.frozen = frozen;
            const linetype = asString(args.linetype); if (linetype !== undefined) patch.linetype = linetype;
            const lineweight = asNumber(args.lineweight); if (lineweight !== undefined) patch.lineweight = lineweight;
            const transparency = asNumber(args.transparency); if (transparency !== undefined) patch.transparency = transparency;
            if (Object.keys(patch).length === 0) return fail('No layer fields provided to update.');
            app.updateLayer(layer.id, patch as never);
            return okText(`Updated layer '${layer.name}': ${Object.keys(patch).join(', ')}.`);
        }
        case 'setActive': {
            if (!target) return fail('setActive requires layerId (id or name).');
            const layer = findLayer();
            if (!layer) return fail(`No layer '${target}'.`);
            app.setActiveLayer(layer.id);
            return okText(`Active layer: '${layer.name}'.`);
        }
        case 'purge': {
            const usedLayers = new Set(
                Object.values(useDocumentStore.getState().sceneObjects).map((o) => o.layerId),
            );
            Object.values(app.entities).forEach((e) => usedLayers.add(e.layer));
            let removed = 0;
            Object.values(app.layers).forEach((l) => {
                if (l.id !== 'layer-0' && !usedLayers.has(l.id) && !usedLayers.has(l.name)) {
                    app.deleteLayer(l.id);
                    removed += 1;
                }
            });
            // Unused block definitions (not referenced by any insert).
            const referenced = new Set(
                Object.values(app.entities)
                    .map((e) => (e.data as Record<string, unknown>)?.blockName)
                    .filter((n): n is string => typeof n === 'string')
            );
            let blocks = 0;
            Object.keys(useBlockStore.getState().defs).forEach((name) => {
                if (!referenced.has(name) && !name.startsWith('*')) {
                    useBlockStore.getState().remove(name);
                    blocks += 1;
                }
            });
            return okText(`Purged ${removed} unused layer(s) and ${blocks} block definition(s).`);
        }
        default:
            return fail('Unknown action. Expected create|update|setActive|purge.');
    }
};

// ---------------------------------------------------------------------------
// document
// ---------------------------------------------------------------------------

export const handleDocumentAction: McpToolHandler = async (args) => {
    const action = asString(args.action);
    switch (action) {
        case 'info': {
            const doc = useDocumentStore.getState();
            const project = bimKernel.getProject();
            return okJson({
                displayName: doc.displayName,
                kind: doc.kind,
                isDirty: doc.isDirty,
                projectName: project.name,
                elements: project.elements.size,
                views: project.views.size,
                sheets: project.sheets.size,
                levels: project.levels.size,
                grids: project.grids.size,
                materials: project.materials.size,
            }, `Document '${doc.displayName}' (${project.elements.size} elements).`);
        }
        case 'save': {
            const saved = await saveCurrentDocument();
            return saved
                ? okText('Document saved.')
                : fail('Save requires an interactive dialog (no bound destination). Use yap_document_action export instead.');
        }
        case 'new': {
            resetCadSession();
            bimKernel.newDocument(asString(args.name));
            useBIMStore.getState()._refresh();
            useDocumentStore.getState().clearSceneLedger();
            return okText('New empty project created.');
        }
        case 'open': {
            const name = asString(args.name);
            if (!name) return fail('open requires a workspace document name.');
            const entry = await findVfsFile(name);
            if (!entry || !entry.content) return fail(`No document named '${name}' in the in-app workspace.`);
            const loaded = await loadDocumentFromData(entry.content, entry.name, entry.id);
            return loaded ? okText(`Opened '${entry.name}' from the workspace.`) : fail(`Failed to open '${entry.name}'.`);
        }
        case 'export': {
            const format = asString(args.format) ?? 'json';
            const project = bimKernel.getProject();
            // CAD-native exports first: they read the drafting ledger.
            if (format === 'dxf') {
                // Version target overrides the 2018 default ('2010', 'R14'…).
                const version = resolveDxfVersion(asString(args.version) ?? DEFAULT_DXF_VERSION);
                const dxfText = writeDxfNative(undefined, { version: version.version });
                return okJson({ format, text: dxfText }, `DXF export (${version.release}) produced ${dxfText.length} characters.`);
            }
            if (format === 'svg') {
                const svg = buildSvgFromCadLedger();
                return okJson({ format, text: svg }, `SVG export produced ${svg.length} characters.`);
            }
            const elements = Array.from(project.elements.values()).map((el) =>
                JSON.parse(safeJsonStringify(el)) as unknown);
            if (format === 'ifc') {
                return okJson({ format, text: exportToIFC(project) }, `IFC4 export produced a schema-valid STEP document (${project.elements.size} elements).`);
            }
            if (format === 'obj') {
                const result = geometryExporter.exportElements(Array.from(project.elements.values()));
                const objText = typeof result.data === 'string' ? result.data : '';
                return okJson({ format, text: objText }, `OBJ export produced ${objText.length} characters.`);
            }
            const doc = useDocumentStore.getState();
            const cadObjects = Object.values(doc.sceneObjects).filter((o) => o.source !== 'bim');
            const payload = {
                document: { displayName: doc.displayName, kind: doc.kind },
                project: JSON.parse(safeJsonStringify({ ...project, elements: new Map() })),
                elements,
                sceneObjects: JSON.parse(safeJsonStringify(cadObjects)),
            };
            return okJson({ format: 'json', text: JSON.stringify(payload) },
                `JSON export: ${elements.length} BIM element(s), ${cadObjects.length} scene object(s).`);
        }
        default:
            return fail('Unknown action. Expected save|new|open|export|info.');
    }
};

/** Minimal SVG of the CAD drafting ledger (mirrors the PLOT/SVG export). */
function buildSvgFromCadLedger(): string {
    const entities = Object.values(useAppStore.getState().entities).filter((e) => e.visible);
    const parts: string[] = [];
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const absorb = (x: number, y: number) => {
        if (x < minX) minX = x; if (x > maxX) maxX = x;
        if (y < minY) minY = y; if (y > maxY) maxY = y;
    };
    const polylines: { pts: number[][]; color: string }[] = [];
    const texts: { x: number; y: number; content: string }[] = [];
    entities.forEach((e) => {
        const d = e.data as Record<string, any>;
        if ((e.type === 'text' || e.type === 'mtext') && Array.isArray(d.position)) {
            texts.push({ x: d.position[0], y: -(d.position[1] ?? 0), content: String(d.content ?? '') });
            absorb(d.position[0], -(d.position[1] ?? 0));
            return;
        }
        const poly = entityToPolyline(e);
        if (!poly || poly.length < 2) return;
        const pts = poly.map((p) => [p[0], -(p[1] ?? 0)]);
        pts.forEach((p) => absorb(p[0], p[1]));
        polylines.push({ pts, color: (d.color as string) ?? '#ffffff' });
    });
    if (!Number.isFinite(minX)) { minX = -60; minY = -60; maxX = 60; maxY = 60; }
    const pad = Math.max((maxX - minX) * 0.08, (maxY - minY) * 0.08, 1);
    const vb = `${minX - pad} ${minY - pad} ${Math.max(maxX - minX + pad * 2, 1)} ${Math.max(maxY - minY + pad * 2, 1)}`;
    polylines.forEach((pl) => {
        const d = pl.pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(3)} ${p[1].toFixed(3)}`).join(' ');
        parts.push(`<path d="${d}" fill="none" stroke="${pl.color}" stroke-width="0.4"/>`);
    });
    texts.forEach((t) => {
        parts.push(`<text x="${t.x.toFixed(2)}" y="${t.y.toFixed(2)}" font-size="2" fill="#ffffff">${t.content.replace(/&/g, '&amp;').replace(/</g, '&lt;')}</text>`);
    });
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${vb}"><g>${parts.join('')}</g></svg>`;
}
